import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Typography, LinearProgress, Paper } from '@mui/material'
import { AccessTime as AccessTimeIcon } from '@mui/icons-material'

interface TestTimerProps {
  timeLimit: number // 制限時間（分）
  startTime: Date
  onTimeUp: () => void
}

export const TestTimer: React.FC<TestTimerProps> = ({ timeLimit, startTime, onTimeUp }) => {
  const { t } = useTranslation('test')
  const [timeLeft, setTimeLeft] = useState(timeLimit * 60) // 秒単位
  const [isWarning, setIsWarning] = useState(false)

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date()
      const elapsed = Math.floor((now.getTime() - startTime.getTime()) / 1000)
      const remaining = Math.max(0, timeLimit * 60 - elapsed)

      setTimeLeft(remaining)

      // 残り5分で警告表示
      if (remaining <= 300 && remaining > 0) {
        setIsWarning(true)
      }

      // 時間切れ
      if (remaining <= 0) {
        clearInterval(interval)
        onTimeUp()
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [timeLimit, startTime, onTimeUp])

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    } else {
      return `${minutes}:${secs.toString().padStart(2, '0')}`
    }
  }

  const getProgressValue = () => {
    const totalSeconds = timeLimit * 60
    return ((totalSeconds - timeLeft) / totalSeconds) * 100
  }

  const getColor = () => {
    if (timeLeft <= 60) return 'error' // 残り1分
    if (timeLeft <= 300) return 'warning' // 残り5分
    return 'primary'
  }

  return (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        position: 'sticky',
        top: 16,
        zIndex: 1000,
        backgroundColor: isWarning ? 'warning.light' : 'background.paper',
      }}
    >
      <Box display="flex" alignItems="center" mb={1}>
        <AccessTimeIcon sx={{ mr: 1, color: getColor() + '.main' }} />
        <Typography variant="h6" color={getColor() + '.main'} fontWeight="bold">
          {t('timer.timeRemaining', { time: formatTime(timeLeft) })}
        </Typography>
      </Box>

      <LinearProgress
        variant="determinate"
        value={getProgressValue()}
        color={getColor()}
        sx={{ height: 8, borderRadius: 4 }}
      />

      {isWarning && timeLeft > 0 && (
        <Typography
          variant="caption"
          color="warning.dark"
          sx={{ mt: 1, display: 'block', fontWeight: 'bold' }}
        >
          {t('timer.timeApproaching')}
        </Typography>
      )}

      {timeLeft <= 0 && (
        <Typography
          variant="caption"
          color="error.main"
          sx={{ mt: 1, display: 'block', fontWeight: 'bold' }}
        >
          {t('timer.timeUp')}
        </Typography>
      )}
    </Paper>
  )
}
